import { useMemo, useRef } from "react";
import PageTitle from "../components/PageTitle";
import DataTable, {
	Cell,
	SearchBar,
} from "../components/table/ServerSideTable";
import { usePurchaseOrderPaginated } from "../hooks/purchase_order.hooks";
import { ColumnDef } from "@tanstack/react-table";
import { PurchaseOrder } from "../types/purchase_orders.types";
import { format } from "date-fns";
import { Flex, Text } from "@chakra-ui/layout";
import ReceiptIcon from "../components/icons/ReceiptIcon";
import { Link, useNavigate } from "react-router-dom";
import PurchaseOrderStatusBadge from "../components/badges/PurchaseOrderStatusBadge";
import BooleanBadge from "../components/badges/BooleanBadge";
import { DownloadIcon, ViewIcon } from "@chakra-ui/icons";
import { useMutation } from "react-query";
import { fetchFileContent } from "../mutations/file.mutations";
import { SavedFile } from "../types/files.types";
import { PaginatedFilter } from "../types/axios.types";
import CenterIDSelect from "../components/inputs/CenterIDSelect";
import i18n from "../i18n";
import { Center } from "../types/center.types";
import { useDataTableContext } from "../hooks/data-table.hooks";
import { useConnectedUser } from "../hooks/user.hooks";

function OrdersDeliveryNotePageEmpty() {
	return (
		<Flex
			direction="column"
			justify="center"
			alignItems="center"
			backgroundColor="white"
			borderRadius="xl"
			p="24px"
			m="4px"
			h="min(40vh, 400px)"
		>
			<Flex
				w="48px"
				h="48px"
				bg="zinc.50"
				border="solid 1px"
				borderColor="brand.100"
				justifyContent="center"
				alignItems="center"
				borderRadius={100}
				mr="8px"
			>
				<ReceiptIcon w="24px" h="24px" color="brand.700" />
			</Flex>
			<Text lineHeight="30px" fontSize="20px" fontWeight="600" color="gray.900">
				Aucun bon de livraison
			</Text>
			<Text lineHeight="30px" fontSize="12px" fontWeight="200" color="gray.400">
				Vous pouvez essayer avec une autre recherche
			</Text>
		</Flex>
	);
}

const CenterIDSelectFilter = (props: {
	filters?: PaginatedFilter;
	setFilters?: (key: string, value: string) => void;
}) => {
	const user = useConnectedUser();

	if (user?.isSuperAdmin === true) {
		return (
			<div style={{ width: "100px" }}>
				<CenterIDSelect
					placeholder={i18n.t("input.select.center.placeholder")}
					centerSelected={props.filters?.centerId as string}
					setCenterSelected={(center?: Center) => {
						if (props.setFilters) {
							props.setFilters("centerId", center?.id.toString() ?? "");
						}
					}}
				/>
			</div>
		);
	}
	return null;
};

function OrdersDeliveryNotePage() {
	const navigate = useNavigate();
	const file = useRef<SavedFile | null>(null);
	const rowId = useRef<number | undefined>(undefined);
	const { mutate: downloadFileContent } = useMutation(fetchFileContent, {
		onSuccess: (content: string) => {
			if (file.current === null) {
				return;
			}
			const blob = new Blob([content]);
			const link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download =
				file.current.createdAt && rowId.current
					? `BL${format(file.current?.createdAt, "yyyy")}${rowId.current}.pdf`
					: file.current.fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
	});
	const columns = useMemo<ColumnDef<PurchaseOrder>[]>(() => {
		return [
			{
				accessorFn: (row) =>
					row.createdAt ? `BL${format(row.createdAt, "yyyy")}${row.id}` : "",
				id: "id",
				header: "ID",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Link color="brand.700" to={`/contacts/centers/${row.center.id}`}>
						{row.center.name}
					</Link>
				),
				id: "center",
				header: "Centre",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.externalId,
				id: "externalId",
				header: "Numéro de suivi",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <PurchaseOrderStatusBadge status={row.status} />,
				id: "status",
				header: "Statut",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) =>
					row.createdAt ? format(row.createdAt, "dd/MM/yyyy") : "--/--/----",
				id: "createdAt",
				header: "Date de création",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<BooleanBadge value={row.commandExportId !== null} />
				),
				id: "hasExport",
				header: "Export",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Flex gap={4} justify="end">
						<DownloadIcon
							w="20px"
							h="20px"
							color="gray.500"
							onClick={() => {
								if (row.file) {
									rowId.current = row.id;
									file.current = row.file;
									downloadFileContent(row.file.id);
								}
							}}
						/>
						<ViewIcon
							w="20px"
							h="20px"
							color="gray.500"
							onClick={() => {
								navigate(`/orders/deliverynote/${row.id}/`);
							}}
						/>
					</Flex>
				),
				id: "actions",
				header: "",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		];
	}, []);
	return (
		<>
			<PageTitle>Bons de livraison</PageTitle>

			<DataTable
				columns={columns}
				paginatedQuery={usePurchaseOrderPaginated}
				w={["10rem", "auto", "18rem", "10rem", "10rem", "10rem", "10rem"]}
				flexGrow={[0, 1, 0, 0, 0, 0, 0]}
				defaultSortBy={[
					{
						id: "id",
						desc: true,
					},
				]}
				noDataComponent={OrdersDeliveryNotePageEmpty}
				cellLink={(cell: Cell) => {
					if (cell.id.endsWith("actions") === true) {
						return;
					}
					return `/orders/deliverynote/${cell.row.id}/`;
				}}
				toolbar={<OrdersDeliveryTableToolbar />}
			/>
		</>
	);
}

const OrdersDeliveryTableToolbar = () => {
	const { filters, setFilters } = useDataTableContext();

	return (
		<Flex my="1rem" gap={2}>
			<CenterIDSelectFilter filters={filters} setFilters={setFilters} />
			<SearchBar />
		</Flex>
	);
};

export default OrdersDeliveryNotePage;
